/**
 * Edit Request Diff Components
 *
 * Specialized renderers for displaying diffs between original and edited product data.
 * Used by admin panel ProductEditRequestsManagement and (mirrored on) seller side.
 */

import React from 'react';
import DOMPurify from 'dompurify';

// =============================================================================
// Types
// =============================================================================

export interface ChangeItem {
  fieldName: string;
  fieldNameFa: string;
  originalValue: any;
  newValue: any;
  hasChanged: boolean;
  isAccountTypeField?: boolean;
  fieldType?: string;
}

export type RiskLevel = 'low' | 'medium' | 'high';

// =============================================================================
// Helpers
// =============================================================================

/** Map a field name (top-level or AccountType field type) to its risk level. */
export function classifyRisk(change: ChangeItem): RiskLevel {
  const { fieldName, fieldType, isAccountTypeField } = change;

  const HIGH = new Set(['name', 'price', 'discountPrice', 'description']);
  const MEDIUM = new Set([
    'images',
    'thumbnail',
    'isPostal',
    'shippingCost',
    'deliveryTime',
    'condition',
    'isOriginal',
    'sku',
  ]);
  const LOW_NAMES = new Set(['stock', 'tags']);

  if (HIGH.has(fieldName)) return 'high';
  if (MEDIUM.has(fieldName)) return 'medium';
  if (LOW_NAMES.has(fieldName)) return 'low';

  if (isAccountTypeField) {
    if (fieldType === 'price' || fieldType === 'capacity_type') return 'high';
    if (fieldType === 'inventory') return 'low';
    return 'medium';
  }
  return 'medium';
}

/** Derive a fieldType for top-level fields when backend didn't supply one. */
export function deriveFieldType(change: ChangeItem): string {
  if (change.fieldType) return change.fieldType;
  switch (change.fieldName) {
    case 'price':
    case 'discountPrice':
    case 'costPrice':
    case 'shippingCost':
      return 'price';
    case 'stock':
      return 'number';
    case 'description':
      return 'longtext';
    case 'images':
      return 'images';
    case 'thumbnail':
      return 'image';
    case 'isPostal':
    case 'isOriginal':
      return 'boolean';
    case 'tags':
      return 'tags';
    default:
      return 'text';
  }
}

/** Fallback Persian labels when backend didn't supply fieldNameFa. */
const FIELD_LABEL_FALLBACKS: Record<string, string> = {
  costPrice: 'قیمت خرید',
};

const formatPrice = (n: number): string =>
  `${new Intl.NumberFormat('fa-IR').format(n)} تومان`;

const formatNumber = (n: number): string => new Intl.NumberFormat('fa-IR').format(n);

// =============================================================================
// RiskBadge — color-coded label per change
// =============================================================================

export const RiskBadge: React.FC<{ level: RiskLevel }> = ({ level }) => {
  const config: Record<RiskLevel, { label: string; bg: string; fg: string }> = {
    low: { label: 'کم‌اثر', bg: 'var(--color-success-bg)', fg: 'var(--color-success-dark)' },
    medium: { label: 'متوسط', bg: 'var(--color-warning-bg)', fg: 'var(--color-warning-dark)' },
    high: { label: 'پراثر', bg: 'var(--color-error-bg)', fg: 'var(--color-error-dark)' },
  };
  const c = config[level];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
        marginInlineStart: 8,
      }}
    >
      {c.label}
    </span>
  );
};

// =============================================================================
// TextDiff — word-level diff for descriptions
// =============================================================================

/**
 * Strip HTML markup so the diff shows what the human reads, not the editor's tags.
 * Description comes from a rich-text editor; we want the visible content diff,
 * not `<p>` vs `<div>` reshuffling.
 */
function stripHtmlToText(input: string): string {
  if (!input) return '';
  return String(input)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<li>/gi, '• ')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

interface DiffWord {
  text: string;
  type: 'same' | 'add' | 'remove';
}

/**
 * Compute word-level diff using a simple LCS table.
 * Returns two arrays of segments — one for the original (with removed words highlighted)
 * and one for the new (with added words highlighted).
 */
function computeWordDiff(oldText: string, newText: string): { oldSegments: DiffWord[]; newSegments: DiffWord[] } {
  const splitWords = (s: string): string[] => s.split(/(\s+)/).filter(Boolean);
  const a = splitWords(oldText || '');
  const b = splitWords(newText || '');

  // LCS DP table — capped to prevent perf disaster on huge texts
  const MAX = 800;
  if (a.length > MAX || b.length > MAX) {
    return {
      oldSegments: [{ text: oldText || '', type: 'same' }],
      newSegments: [{ text: newText || '', type: 'same' }],
    };
  }

  const dp: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const oldSegments: DiffWord[] = [];
  const newSegments: DiffWord[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      oldSegments.push({ text: a[i], type: 'same' });
      newSegments.push({ text: b[j], type: 'same' });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      oldSegments.push({ text: a[i], type: 'remove' });
      i++;
    } else {
      newSegments.push({ text: b[j], type: 'add' });
      j++;
    }
  }
  while (i < a.length) oldSegments.push({ text: a[i++], type: 'remove' });
  while (j < b.length) newSegments.push({ text: b[j++], type: 'add' });

  return { oldSegments, newSegments };
}

const renderSegments = (segs: DiffWord[]): React.ReactNode =>
  segs.map((seg, idx) => {
    if (seg.type === 'same') return <span key={idx}>{seg.text}</span>;
    if (seg.type === 'add')
      return (
        <span
          key={idx}
          style={{ background: 'var(--color-success-bg)', color: 'var(--color-success-dark)', borderRadius: 2, padding: '0 2px' }}
        >
          {seg.text}
        </span>
      );
    return (
      <span
        key={idx}
        style={{
          background: 'var(--color-error-bg)',
          color: 'var(--color-error-dark)',
          textDecoration: 'line-through',
          borderRadius: 2,
          padding: '0 2px',
        }}
      >
        {seg.text}
      </span>
    );
  });

export const TextDiff: React.FC<{ oldText?: string; newText?: string }> = ({ oldText, newText }) => {
  const { oldSegments, newSegments } = React.useMemo(
    () => computeWordDiff(stripHtmlToText(String(oldText || '')), stripHtmlToText(String(newText || ''))),
    [oldText, newText]
  );
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div
        style={{
          background: 'var(--color-bg-default)',
          padding: '10px 12px',
          borderRadius: 8,
          lineHeight: 1.85,
          whiteSpace: 'pre-wrap',
          fontSize: 13,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>قبل</div>
        {oldSegments.length > 0 ? renderSegments(oldSegments) : <em style={{ color: 'var(--color-text-disabled)' }}>خالی</em>}
      </div>
      <div
        style={{
          background: 'var(--color-bg-default)',
          padding: '10px 12px',
          borderRadius: 8,
          lineHeight: 1.85,
          whiteSpace: 'pre-wrap',
          fontSize: 13,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>بعد</div>
        {newSegments.length > 0 ? renderSegments(newSegments) : <em style={{ color: 'var(--color-text-disabled)' }}>خالی</em>}
      </div>
    </div>
  );
};

// =============================================================================
// ImagesDiff — added (green border) / removed (red strikethrough)
// =============================================================================

const normalizeImages = (v: any): string[] => {
  if (Array.isArray(v)) return v.filter(Boolean) as string[];
  if (typeof v === 'string' && v) return [v];
  return [];
};

export const ImagesDiff: React.FC<{ oldImages?: any; newImages?: any }> = ({ oldImages, newImages }) => {
  const oldList = normalizeImages(oldImages);
  const newList = normalizeImages(newImages);
  const oldSet = new Set(oldList);
  const newSet = new Set(newList);

  const removed = oldList.filter(u => !newSet.has(u));
  const added = newList.filter(u => !oldSet.has(u));
  const kept = newList.filter(u => oldSet.has(u));

  const orderChanged =
    removed.length === 0 &&
    added.length === 0 &&
    oldList.join('|') !== newList.join('|');

  const cell = (url: string, mode: 'kept' | 'added' | 'removed') => {
    const borders: Record<typeof mode, string> = {
      kept: '2px solid var(--color-border)',
      added: '3px solid var(--color-success-dark)',
      removed: '3px solid var(--color-error-dark)',
    };
    return (
      <div
        key={url + mode}
        style={{
          position: 'relative',
          width: 88,
          height: 88,
          borderRadius: 8,
          overflow: 'hidden',
          border: borders[mode],
          background: 'var(--color-bg-default)',
          flexShrink: 0,
        }}
      >
        <img
          src={url}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: mode === 'removed' ? 0.5 : 1,
            filter: mode === 'removed' ? 'grayscale(0.6)' : 'none',
          }}
        />
        {mode === 'removed' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top right, transparent calc(50% - 1px), var(--color-error-dark) calc(50% - 1px), var(--color-error-dark) calc(50% + 1px), transparent calc(50% + 1px))',
            }}
          />
        )}
        <span
          style={{
            position: 'absolute',
            top: 4,
            insetInlineEnd: 4,
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 6,
            color: '#fff',
            background: mode === 'added' ? 'var(--color-success-dark)' : mode === 'removed' ? 'var(--color-error-dark)' : 'var(--color-text-secondary)',
          }}
        >
          {mode === 'added' ? 'اضافه' : mode === 'removed' ? 'حذف' : 'بدون تغییر'}
        </span>
      </div>
    );
  };

  if (oldList.length === 0 && newList.length === 0) {
    return <em style={{ color: 'var(--color-text-disabled)' }}>تصویری ثبت نشده</em>;
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {removed.map(u => cell(u, 'removed'))}
        {added.map(u => cell(u, 'added'))}
        {kept.map(u => cell(u, 'kept'))}
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span>
          <strong style={{ color: 'var(--color-success-dark)' }}>+{added.length}</strong> اضافه‌شده
        </span>
        <span>
          <strong style={{ color: 'var(--color-error-dark)' }}>−{removed.length}</strong> حذف‌شده
        </span>
        <span>
          <strong>{kept.length}</strong> بدون تغییر
        </span>
        {orderChanged && (
          <span style={{ color: 'var(--color-warning-dark)' }}>ترتیب تصاویر تغییر کرده است</span>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// CapacityDiff — tier-by-tier comparison for capacity_type fields
// =============================================================================

interface CapacityEntry {
  key: string;
  name?: string;
  value?: any;
  price?: number;
  stock?: number;
  description?: string;
}

const normalizeCapacities = (v: any): Record<string, CapacityEntry> => {
  if (!v || typeof v !== 'object') return {};
  // New shape: { tier1: { name, value, price, stock?, description? }, ... }
  if (!Array.isArray(v)) {
    const out: Record<string, CapacityEntry> = {};
    for (const [k, raw] of Object.entries(v)) {
      if (!raw) continue;
      if (typeof raw === 'object' && raw !== null) {
        out[k] = { key: k, ...(raw as any) };
      } else {
        out[k] = { key: k, value: raw };
      }
    }
    return out;
  }
  // Legacy shape: array of entries
  const out: Record<string, CapacityEntry> = {};
  for (const entry of v) {
    if (!entry || typeof entry !== 'object') continue;
    const k = entry.key || entry.value || JSON.stringify(entry);
    out[k] = { key: k, ...entry };
  }
  return out;
};

const fieldVal = (e?: CapacityEntry, key?: keyof CapacityEntry): string => {
  if (!e || !key) return '—';
  const v = e[key];
  if (v === undefined || v === null || v === '') return '—';
  if (key === 'price') return formatPrice(Number(v) || 0);
  if (key === 'stock') return formatNumber(Number(v) || 0);
  return String(v);
};

const isDifferent = (a: any, b: any): boolean =>
  String(a ?? '') !== String(b ?? '');

export const CapacityDiff: React.FC<{ oldValue?: any; newValue?: any }> = ({ oldValue, newValue }) => {
  const oldMap = normalizeCapacities(oldValue);
  const newMap = normalizeCapacities(newValue);
  const allKeys = Array.from(new Set([...Object.keys(oldMap), ...Object.keys(newMap)]));

  if (allKeys.length === 0) {
    return <em style={{ color: 'var(--color-text-disabled)' }}>هیچ حجم / سایزی تعریف نشده</em>;
  }

  const cellStyle: React.CSSProperties = {
    padding: '8px 10px',
    fontSize: 12,
    borderInlineStart: '1px solid var(--color-border)',
    verticalAlign: 'top',
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          background: 'var(--color-bg-paper)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          fontSize: 12,
        }}
      >
        <thead style={{ background: 'var(--color-bg-default)' }}>
          <tr>
            <th style={{ ...cellStyle, textAlign: 'start', fontWeight: 700 }}>تیر</th>
            <th style={{ ...cellStyle, textAlign: 'start', fontWeight: 700 }}>قیمت قبل / بعد</th>
            <th style={{ ...cellStyle, textAlign: 'start', fontWeight: 700 }}>موجودی قبل / بعد</th>
            <th style={{ ...cellStyle, textAlign: 'start', fontWeight: 700 }}>وضعیت</th>
          </tr>
        </thead>
        <tbody>
          {allKeys.map(k => {
            const o = oldMap[k];
            const n = newMap[k];
            let status: 'added' | 'removed' | 'changed' | 'same' = 'same';
            if (!o && n) status = 'added';
            else if (o && !n) status = 'removed';
            else if (
              isDifferent(o?.price, n?.price) ||
              isDifferent(o?.stock, n?.stock) ||
              isDifferent(o?.name, n?.name) ||
              isDifferent(o?.description, n?.description)
            )
              status = 'changed';

            const rowBg =
              status === 'added'
                ? 'rgba(52, 168, 83, 0.06)'
                : status === 'removed'
                ? 'rgba(234, 67, 53, 0.06)'
                : status === 'changed'
                ? 'rgba(251, 188, 4, 0.06)'
                : 'transparent';

            const statusLabel: Record<typeof status, string> = {
              added: 'تیر جدید',
              removed: 'حذف شده',
              changed: 'تغییر کرده',
              same: 'بدون تغییر',
            };
            const statusColor: Record<typeof status, string> = {
              added: 'var(--color-success-dark)',
              removed: 'var(--color-error-dark)',
              changed: 'var(--color-warning-dark)',
              same: 'var(--color-text-secondary)',
            };

            const priceChanged = o && n && isDifferent(o.price, n.price);
            const stockChanged = o && n && isDifferent(o.stock, n.stock);
            const nameChanged = o && n && isDifferent(o.name, n.name);
            const displayName = (n?.name || o?.name || k) as string;

            return (
              <tr key={k} style={{ borderTop: '1px solid var(--color-border)', background: rowBg }}>
                <td style={cellStyle}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-text-primary)' }}>{displayName}</div>
                  {nameChanged && o?.name && (
                    <div style={{ fontSize: 11, color: 'var(--color-error-dark)', textDecoration: 'line-through' }}>{o.name}</div>
                  )}
                </td>
                <td style={cellStyle}>
                  <div style={{ color: priceChanged ? 'var(--color-error-dark)' : 'var(--color-text-secondary)', textDecoration: priceChanged ? 'line-through' : 'none' }}>
                    {fieldVal(o, 'price')}
                  </div>
                  <div style={{ color: priceChanged ? 'var(--color-success-dark)' : 'var(--color-text-secondary)', fontWeight: priceChanged ? 700 : 400 }}>
                    {fieldVal(n, 'price')}
                  </div>
                </td>
                <td style={cellStyle}>
                  <div style={{ color: stockChanged ? 'var(--color-error-dark)' : 'var(--color-text-secondary)', textDecoration: stockChanged ? 'line-through' : 'none' }}>
                    {fieldVal(o, 'stock')}
                  </div>
                  <div style={{ color: stockChanged ? 'var(--color-success-dark)' : 'var(--color-text-secondary)', fontWeight: stockChanged ? 700 : 400 }}>
                    {fieldVal(n, 'stock')}
                  </div>
                </td>
                <td style={cellStyle}>
                  <span style={{ color: statusColor[status], fontWeight: 600 }}>{statusLabel[status]}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// SimpleDiff — fallback renderer for primitive values (text, number, price, ...)
// =============================================================================

/**
 * نمایش مقدار به‌صورت خوانا برای انسان.
 * اولویت در آبجکت‌ها: label > name > title > description > value (آخرین چاره).
 */
const objectToReadable = (v: any): string => {
  if (!v || typeof v !== 'object') return String(v ?? '—');
  // dropdown / radio: { label, value }
  if (typeof v.label === 'string' && v.label.trim()) return v.label;
  // capacity entry: { name, value, price?, stock?, description? }
  if (typeof v.name === 'string' && v.name.trim()) {
    if (typeof v.price === 'number') return `${v.name} — ${formatPrice(v.price)}`;
    return v.name;
  }
  if (typeof v.title === 'string' && v.title.trim()) return v.title;
  if (typeof v.description === 'string' && v.description.trim()) return v.description;
  if ('value' in v) return String(v.value);
  return JSON.stringify(v).slice(0, 80);
};

const formatPrimitive = (v: any, fieldType?: string): string => {
  if (typeof v === 'boolean') return v ? 'دارد' : 'ندارد';
  if (v === null || v === undefined || v === '') return '—';
  if (fieldType === 'price' && typeof v === 'number') return formatPrice(v);
  if (fieldType === 'number' && typeof v === 'number') return formatNumber(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return '—';
    return v.map(item => (typeof item === 'object' && item !== null ? objectToReadable(item) : String(item))).join('، ');
  }
  if (typeof v === 'object') return objectToReadable(v);
  // ✅ اگر متن HTML دارد (مثل description از rich editor)، تگ‌ها را پاک کن
  const s = String(v);
  if (/<[a-z][\s\S]*>/i.test(s)) return stripHtmlToText(s);
  return s;
};

export const SimpleDiff: React.FC<{ oldValue?: any; newValue?: any; fieldType?: string }> = ({
  oldValue,
  newValue,
  fieldType,
}) => {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, alignItems: 'center' }}>
      <div
        style={{
          background: 'var(--color-error-bg)',
          padding: '8px 10px',
          borderRadius: 6,
          fontSize: 13,
          color: 'var(--color-error-dark)',
          textDecoration: 'line-through',
          minHeight: 36,
          wordBreak: 'break-word',
        }}
      >
        {formatPrimitive(oldValue, fieldType)}
      </div>
      <span style={{ fontSize: 18, color: 'var(--color-text-disabled)' }}>←</span>
      <div
        style={{
          background: 'var(--color-success-bg)',
          padding: '8px 10px',
          borderRadius: 6,
          fontSize: 13,
          color: 'var(--color-success-dark)',
          fontWeight: 600,
          minHeight: 36,
          wordBreak: 'break-word',
        }}
      >
        {formatPrimitive(newValue, fieldType)}
      </div>
    </div>
  );
};

// =============================================================================
// ShippingSettingsDiff — table view for nested shipping object
// =============================================================================

const SHIPPING_FIELD_LABELS: Record<string, string> = {
  isPostalEnabled: 'ارسال پستی',
  shippingCost: 'هزینه ارسال',
  estimatedDeliveryDays: 'زمان تخمینی ارسال (روز)',
  freeShippingThreshold: 'آستانه ارسال رایگان',
  packagingNotes: 'یادداشت بسته‌بندی',
  workingDays: 'روزهای کاری',
};

const formatShippingValue = (key: string, v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'فعال' : 'غیرفعال';
  if ((key === 'shippingCost' || key === 'freeShippingThreshold') && typeof v === 'number') {
    return formatPrice(v);
  }
  if (typeof v === 'number') return formatNumber(v);
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.join('، ');
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
};

export const ShippingSettingsDiff: React.FC<{ oldValue?: any; newValue?: any }> = ({
  oldValue,
  newValue,
}) => {
  const o = (oldValue && typeof oldValue === 'object') ? oldValue : {};
  const n = (newValue && typeof newValue === 'object') ? newValue : {};
  const keys = Array.from(new Set([...Object.keys(o), ...Object.keys(n)]));
  if (keys.length === 0) {
    return <em style={{ color: 'var(--color-text-disabled)' }}>تنظیماتی وجود ندارد</em>;
  }
  return (
    <div
      style={{
        background: 'var(--color-bg-paper)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {keys.map((k, i) => {
        const ov = o[k];
        const nv = n[k];
        const changed = JSON.stringify(ov ?? null) !== JSON.stringify(nv ?? null);
        return (
          <div
            key={k}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 12,
              padding: '10px 12px',
              borderTop: i === 0 ? 'none' : '1px solid var(--color-border)',
              alignItems: 'center',
              background: changed ? 'rgba(251, 188, 4, 0.06)' : 'transparent',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {SHIPPING_FIELD_LABELS[k] || k}
            </div>
            <div
              style={{
                fontSize: 12,
                padding: '6px 8px',
                borderRadius: 6,
                background: changed ? 'var(--color-error-bg)' : 'var(--color-bg-default)',
                color: changed ? 'var(--color-error-dark)' : 'var(--color-text-secondary)',
                textDecoration: changed ? 'line-through' : 'none',
              }}
            >
              {formatShippingValue(k, ov)}
            </div>
            <div
              style={{
                fontSize: 12,
                padding: '6px 8px',
                borderRadius: 6,
                background: changed ? 'var(--color-success-bg)' : 'var(--color-bg-default)',
                color: changed ? 'var(--color-success-dark)' : 'var(--color-text-secondary)',
                fontWeight: changed ? 700 : 400,
              }}
            >
              {formatShippingValue(k, nv)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// RefArrayDiff — render [{id, name}, ...] arrays (e.g. activityAreas) as chips
// =============================================================================

type RefItem = { id: string; name: string };

const normalizeRefArray = (v: any): RefItem[] => {
  if (!Array.isArray(v)) return [];
  return v
    .map((item) => {
      if (item && typeof item === 'object') {
        return { id: String(item.id || item._id || ''), name: String(item.name || item.id || item._id || '') };
      }
      const s = String(item);
      return { id: s, name: s };
    })
    .filter((x) => x.id || x.name);
};

export const RefArrayDiff: React.FC<{ oldValue?: any; newValue?: any }> = ({
  oldValue,
  newValue,
}) => {
  const oldList = normalizeRefArray(oldValue);
  const newList = normalizeRefArray(newValue);
  const oldIds = new Set(oldList.map((x) => x.id));
  const newIds = new Set(newList.map((x) => x.id));

  const removed = oldList.filter((x) => !newIds.has(x.id));
  const added = newList.filter((x) => !oldIds.has(x.id));
  const kept = newList.filter((x) => oldIds.has(x.id));

  const chip = (item: RefItem, mode: 'kept' | 'added' | 'removed') => {
    const styles: Record<typeof mode, React.CSSProperties> = {
      kept: { background: 'var(--color-bg-default)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' },
      added: { background: 'var(--color-success-bg)', color: 'var(--color-success-dark)', border: '1px solid var(--color-success-dark)' },
      removed: { background: 'var(--color-error-bg)', color: 'var(--color-error-dark)', border: '1px solid var(--color-error-dark)', textDecoration: 'line-through' },
    };
    return (
      <span
        key={`${mode}-${item.id}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 10px',
          borderRadius: 14,
          fontSize: 12,
          fontWeight: 500,
          ...styles[mode],
        }}
      >
        {item.name || item.id}
      </span>
    );
  };

  if (removed.length === 0 && added.length === 0 && kept.length === 0) {
    return <em style={{ color: 'var(--color-text-disabled)' }}>—</em>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {removed.map((x) => chip(x, 'removed'))}
        {added.map((x) => chip(x, 'added'))}
        {kept.map((x) => chip(x, 'kept'))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'flex', gap: 14 }}>
        {added.length > 0 && (
          <span><strong style={{ color: 'var(--color-success-dark)' }}>+{added.length}</strong> اضافه</span>
        )}
        {removed.length > 0 && (
          <span><strong style={{ color: 'var(--color-error-dark)' }}>−{removed.length}</strong> حذف</span>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// StringArrayDiff — chips for plain string[] fields (e.g. categories tags)
// =============================================================================

const normalizeStringArray = (v: any): string[] => {
  if (Array.isArray(v)) return v.map((x) => String(x)).filter((x) => x.trim() !== '');
  if (typeof v === 'string' && v.trim()) return [v];
  return [];
};

export const StringArrayDiff: React.FC<{ oldValue?: any; newValue?: any }> = ({
  oldValue,
  newValue,
}) => {
  const oldList = normalizeStringArray(oldValue);
  const newList = normalizeStringArray(newValue);
  return (
    <RefArrayDiff
      oldValue={oldList.map((s) => ({ id: s, name: s }))}
      newValue={newList.map((s) => ({ id: s, name: s }))}
    />
  );
};

// =============================================================================
// ChangeRow — top-level dispatcher: picks the right renderer per fieldType
// =============================================================================

export const ChangeRow: React.FC<{ change: ChangeItem }> = ({ change }) => {
  const fieldType = deriveFieldType(change);
  const risk = classifyRisk(change);

  let body: React.ReactNode;
  switch (fieldType) {
    case 'capacity_type':
      body = <CapacityDiff oldValue={change.originalValue} newValue={change.newValue} />;
      break;
    case 'images':
    case 'image':
      body = <ImagesDiff oldImages={change.originalValue} newImages={change.newValue} />;
      break;
    case 'shipping':
      body = <ShippingSettingsDiff oldValue={change.originalValue} newValue={change.newValue} />;
      break;
    case 'ref-array':
      body = <RefArrayDiff oldValue={change.originalValue} newValue={change.newValue} />;
      break;
    case 'string-array':
      body = <StringArrayDiff oldValue={change.originalValue} newValue={change.newValue} />;
      break;
    case 'longtext':
    case 'textarea':
      body = <TextDiff oldText={change.originalValue} newText={change.newValue} />;
      break;
    default:
      body = <SimpleDiff oldValue={change.originalValue} newValue={change.newValue} fieldType={fieldType} />;
  }

  return (
    <div
      style={{
        background: 'var(--color-bg-paper)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        padding: 14,
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-primary)' }}>
          {change.fieldNameFa || FIELD_LABEL_FALLBACKS[change.fieldName] || change.fieldName}
        </span>
        {change.isAccountTypeField && (
          <span
            style={{
              fontSize: 10,
              background: 'var(--color-primary-bg)',
              color: 'var(--color-primary)',
              padding: '2px 6px',
              borderRadius: 4,
              fontWeight: 500,
            }}
          >
            فیلد تخصصی
          </span>
        )}
        <RiskBadge level={risk} />
      </div>
      {body}
    </div>
  );
};

// =============================================================================
// CapacityValueView — single-value display for capacity_type fields (no diff)
// =============================================================================

export const CapacityValueView: React.FC<{ value?: any }> = ({ value }) => {
  const map = normalizeCapacities(value);
  const keys = Object.keys(map);

  if (keys.length === 0) {
    return <em style={{ color: 'var(--color-text-disabled)' }}>هیچ حجم / سایزی تعریف نشده</em>;
  }

  const cellStyle: React.CSSProperties = {
    padding: '8px 10px',
    fontSize: 12,
    borderInlineStart: '1px solid var(--color-border)',
    verticalAlign: 'top',
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          background: 'var(--color-bg-paper)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          fontSize: 12,
        }}
      >
        <thead style={{ background: 'var(--color-bg-default)' }}>
          <tr>
            <th style={{ ...cellStyle, textAlign: 'start', fontWeight: 700 }}>تیر</th>
            <th style={{ ...cellStyle, textAlign: 'start', fontWeight: 700 }}>قیمت</th>
            <th style={{ ...cellStyle, textAlign: 'start', fontWeight: 700 }}>موجودی</th>
            <th style={{ ...cellStyle, textAlign: 'start', fontWeight: 700 }}>توضیحات</th>
          </tr>
        </thead>
        <tbody>
          {keys.map(k => {
            const e = map[k];
            return (
              <tr key={k} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={cellStyle}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-text-primary)' }}>{e?.name || k}</div>
                </td>
                <td style={cellStyle}>{fieldVal(e, 'price')}</td>
                <td style={cellStyle}>{fieldVal(e, 'stock')}</td>
                <td style={cellStyle}>{e?.description || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// ImagesGalleryView — single-value image gallery (no diff)
// =============================================================================

export const ImagesGalleryView: React.FC<{ images?: any; onClick?: (url: string) => void }> = ({ images, onClick }) => {
  const list = normalizeImages(images);
  if (list.length === 0) return <em style={{ color: 'var(--color-text-disabled)' }}>تصویری ثبت نشده</em>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {list.map((url, i) => (
        <div
          key={i}
          onClick={onClick ? () => onClick(url) : undefined}
          style={{
            width: 88,
            height: 88,
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-default)',
            flexShrink: 0,
            cursor: onClick ? 'zoom-in' : 'default',
          }}
        >
          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// ProductFieldsView — render dynamic AccountType fields of a product (read-only)
// Use case: admin product approval queue, or any place needing pretty value display.
// =============================================================================

interface AccountTypeFieldDef {
  name: string;
  label?: string;
  type?: string;
  [key: string]: any;
}

// ✅ Walk بازگشتی روی subFields تا فیلدهای nested (فرم تو در تو) هم رندر شوند
const flattenFieldDefs = (list: AccountTypeFieldDef[] | undefined): AccountTypeFieldDef[] => {
  if (!Array.isArray(list)) return [];
  const out: AccountTypeFieldDef[] = [];
  for (const f of list) {
    if (!f) continue;
    out.push(f);
    if (Array.isArray((f as any).subFields) && (f as any).subFields.length) {
      out.push(...flattenFieldDefs((f as any).subFields));
    }
  }
  return out;
};

const isBoolField = (def: AccountTypeFieldDef): boolean => {
  if (def.type !== 'checkbox' && def.type !== 'radio') return false;
  const items = (def as any).options?.items;
  return !items || items.length === 0;
};

const renderEnumValue = (def: AccountTypeFieldDef, val: any): string => {
  const items = (def as any).options?.items;
  if (Array.isArray(val)) {
    if (val.length === 0) return '—';
    return val.map((v: any) => {
      if (typeof v === 'object' && v !== null) return v.label || v.value || '';
      const it = Array.isArray(items) ? items.find((i: any) => i.value === v || i.value === String(v)) : null;
      return it?.label || String(v);
    }).filter(Boolean).join('، ');
  }
  if (typeof val === 'object' && val !== null) return val.label || val.value || '—';
  if (Array.isArray(items)) {
    const it = items.find((i: any) => i.value === val || i.value === String(val));
    if (it) return it.label;
  }
  return String(val);
};

export const ProductFieldsView: React.FC<{
  fieldsData?: Record<string, any> | null;
  accountTypeFields?: AccountTypeFieldDef[];
}> = ({ fieldsData, accountTypeFields }) => {
  const data = fieldsData || {};
  // ✅ پهن‌سازی بازگشتی: فیلدهای داخل subFields هم در لیست می‌آیند
  const defs = flattenFieldDefs(accountTypeFields).filter(d => d && !(d as any).clientOnly);

  // Iterate based on accountType definitions to preserve order; fall back to data keys
  // ✅ فیلدهای بولین حتی اگر مقدارشان در data نباشد (false/undefined) نمایش داده شوند
  const orderedKeys = defs.length > 0
    ? defs.filter(d => d.name in data || isBoolField(d)).map(d => d.name)
    : Object.keys(data);

  if (orderedKeys.length === 0) {
    return <em style={{ color: 'var(--color-text-disabled)', fontSize: 13 }}>فیلدی برای نمایش وجود ندارد</em>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {orderedKeys.map(key => {
        const def = defs.find(d => d.name === key) || ({ name: key } as AccountTypeFieldDef);
        const label = def.label || key;
        const type = def.type || 'text';
        const value = data[key];

        let body: React.ReactNode;

        if (isBoolField(def)) {
          // ✅ Boolean checkbox/radio (no items): دارد/ندارد با badge رنگی
          const yes = value === true || value === 'true';
          body = (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: yes ? 'var(--color-success-bg)' : 'var(--color-bg-default)',
              color: yes ? 'var(--color-success-dark)' : 'var(--color-text-disabled)',
              border: `1px solid ${yes ? 'var(--color-success-dark)' : 'var(--color-border)'}`,
            }}>
              <span style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: yes ? 'var(--color-success-dark)' : 'var(--color-text-disabled)',
              }} />
              {yes ? 'دارد' : 'ندارد'}
            </div>
          );
        } else if (type === 'capacity_type') {
          body = <CapacityValueView value={value} />;
        } else if (type === 'images' || type === 'image') {
          body = <ImagesGalleryView images={value} />;
        } else if (type === 'longtext' || type === 'textarea') {
          // ✅ اگر مقدار HTML دارد، rich نمایش می‌دهیم؛ اگر متن خام است، pre-wrap.
          const raw = value ? String(value) : '';
          const looksLikeHtml = /<[a-z][\s\S]*>/i.test(raw);
          body = looksLikeHtml ? (
            <div
              style={{
                background: 'var(--color-bg-default)',
                padding: '8px 10px',
                borderRadius: 6,
                fontSize: 13,
                lineHeight: 1.85,
                wordBreak: 'break-word',
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(raw) }}
            />
          ) : (
            <div
              style={{
                whiteSpace: 'pre-wrap',
                background: 'var(--color-bg-default)',
                padding: '8px 10px',
                borderRadius: 6,
                fontSize: 13,
                lineHeight: 1.8,
              }}
            >
              {raw || <em style={{ color: 'var(--color-text-disabled)' }}>—</em>}
            </div>
          );
        } else if (type === 'credentials') {
          body = (
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
              اطلاعات حساب (مخفی)
            </div>
          );
        } else if (type === 'dropdown' || type === 'radio' || type === 'checkbox') {
          // ✅ dropdown / multi-select checkbox / radio با items → label گزینه‌ها
          if (value === undefined || value === null || value === '') {
            body = <div style={{ fontSize: 13, color: 'var(--color-text-disabled)' }}>—</div>;
          } else {
            body = (
              <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
                {renderEnumValue(def, value)}
              </div>
            );
          }
        } else if (type === 'password') {
          body = <div style={{ fontSize: 13, color: 'var(--color-text-primary)', letterSpacing: 2 }}>••••••</div>;
        } else if (type === 'color') {
          // ✅ نمایش حرفه‌ای رنگ: swatch دایره‌ای + کد hex مونوسپیس
          const isValidHex = typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);
          if (!isValidHex) {
            body = <div style={{ fontSize: 13, color: 'var(--color-text-disabled)' }}>—</div>;
          } else {
            body = (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: value,
                  border: value.toLowerCase() === '#ffffff' ? '1px solid var(--color-border)' : 'none',
                  boxShadow: '0 0 0 2px var(--color-bg-paper), 0 0 0 3px rgba(0,0,0,0.1)',
                  flexShrink: 0,
                }} />
                <span style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  fontFamily: 'monospace',
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  direction: 'ltr',
                }}>
                  {value}
                </span>
              </div>
            );
          }
        } else if (type === 'date') {
          let displayDate = '—';
          try {
            displayDate = value ? new Date(value).toLocaleDateString('fa-IR') : '—';
          } catch { displayDate = String(value || '—'); }
          body = <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{displayDate}</div>;
        } else {
          body = (
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
              {formatPrimitive(value, type === 'price' || type === 'inventory' || type === 'number' || type === 'value' ? type : undefined)}
            </div>
          );
        }

        return (
          <div
            key={key}
            style={{
              background: 'var(--color-bg-paper)',
              border: '1px solid var(--color-border)',
              borderRadius: 10,
              padding: '12px 14px',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
              {label}
            </div>
            {body}
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// ChangesList — renders all changes with a count header
// =============================================================================

export const ChangesList: React.FC<{ changes: ChangeItem[] }> = ({ changes }) => {
  const visible = (changes || []).filter(c => c && c.hasChanged !== false);

  if (visible.length === 0) {
    return (
      <div
        style={{
          padding: 20,
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
          background: 'var(--color-bg-default)',
          borderRadius: 8,
        }}
      >
        تغییری برای مقایسه وجود ندارد
      </div>
    );
  }

  // Sort by risk: high → medium → low
  const sorted = [...visible].sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 } as const;
    return order[classifyRisk(a)] - order[classifyRisk(b)];
  });

  return (
    <div>
      {sorted.map((c, idx) => (
        <ChangeRow key={`${c.fieldName}-${idx}`} change={c} />
      ))}
    </div>
  );
};
